<template>
  <b-modal v-model="modal" :title="title" size="lg" :no-enforce-focus="true" :no-close-on-backdrop="true">
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false">
    </loading>
    <div class="row">
      <div class="col-lg-12">
        <form @submit.prevent="saveCampaign()" autocomplete="off">
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label for="name">Nome da campanha</label>
                <input
                  id="name"
                  v-model="campaign.name"
                  name="name"
                  type="text"
                  class="form-control"
                  :class="{
                    'is-invalid': submitted && $v.campaign.name.$error
                  }"
                />
                <div
                  v-if="submitted && !$v.campaign.name.required"
                  class="invalid-feedback"
                >
                  O campo nome é obrigatório.
                </div>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label for="type_contact">Destinatários</label>
                <select
                  id="type_contact"
                  v-model="campaign.type_contact"
                  name="type_contact"
                  type="text"
                  class="custom-select"
                  :class="{
                    'is-invalid': submitted && $v.campaign.type_contact.$error
                  }"
                >
                  <option></option>
                  <option value="guests">Convidados</option>
                  <option value="registered">Inscritos</option>
                </select>
                <div
                  v-if="submitted && !$v.campaign.type_contact.required"
                  class="invalid-feedback"
                >
                  O campo é obrigatório.
                </div>
              </div>
            </div>
          </div>
          <!-- Filtros combinados (nova UI: chips compactos + adicionar) -->
          <campaign-filter-builder
            v-model="campaign.rules"
            :catalog="filterCatalog"
            :loading="catalogLoading"
          />

          <!-- Preview de contagem -->
          <div class="count-box" v-if="campaign.type_contact">
            <i class="bx bx-group"></i>
            <span v-if="countLoading"><i class="bx bx-loader-alt bx-spin"></i> Calculando...</span>
            <span v-else-if="participantCount > 0"><strong>{{ participantCount }}</strong> {{ participantCount === 1 ? 'participante será afetado' : 'participantes serão afetados' }}</span>
            <span v-else class="text-warning">Nenhum participante encontrado com os filtros selecionados</span>
          </div>

          <div class="row">
            <div class="col-sm-12">
              <div class="form-group">
                <label for="template_id">Template</label>
                <select
                  class="custom-select"
                  name="template_id"
                  v-model="campaign.template_id"
                  @change="setTemplate(campaign.template_id)"
                  :class="{
                    'is-invalid': submitted && $v.campaign.template_id.$error
                  }"
                >
                  <option></option>
                  <option v-for="(t,i) in templates" :key=i :value="t.id">{{ t.namespace }}</option>
                </select>
                <div
                  v-if="submitted && !$v.campaign.template_id.required"
                  class="invalid-feedback"
                >
                  O campo é obrigatório.
                </div>
                <button type="button" class="btn btn-outline-dark btn-sm mt-2" v-if="campaign.template_id" @click="showExampleTemplate">
                  <i class="bx bx-show link" ></i> Ver exemplo do template
                </button>
              </div>
            </div>
          </div>
          <!-- QR dinâmico: aviso (não pede banner) -->
          <div class="row" v-if="showHeader && isDynamicQrTemplate">
            <div class="col-sm-12">
              <div class="cf-qr-info">
                <div class="cf-qr-info-icon">
                  <i class="bx bx-qr-scan"></i>
                </div>
                <div>
                  <div class="cf-qr-info-title">Header dinâmico ativado</div>
                  <div class="cf-qr-info-desc">
                    Este template usa <strong>QR Code do participante</strong> no header.
                    Cada destinatário receberá automaticamente o próprio QR único —
                    você não precisa anexar nenhuma imagem aqui.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="row" v-if="showHeader && !isDynamicQrTemplate">
            <div class="col-sm-12">
              <div class="form-group">
                <label for="header_banner">Header {{ headerFormatLabel }}</label>

                <!-- IMAGE -->
                <template v-if="headerFormat === 'IMAGE'">
                  <div v-if="templateHeaderMediaUrl && !headerFileChanged" class="mb-2">
                    <img
                      :src="templateHeaderMediaUrl"
                      style="max-height:120px;border-radius:6px;border:1px solid #e9ecef;cursor:pointer"
                      @click="showImagePreview = true"
                      title="Clique para ampliar"
                    />
                    <small class="text-muted d-block mt-1">Imagem padrão do template · <a href="#" @click.prevent="showImagePreview = true">Ampliar</a></small>
                    <button type="button" class="btn btn-sm btn-outline-secondary mt-1" @click="headerFileChanged = true">
                      <i class="bx bx-image-add mr-1"></i> Trocar imagem
                    </button>
                    <b-modal v-model="showImagePreview" title="Imagem do template" size="lg" hide-footer centered>
                      <img :src="templateHeaderMediaUrl" style="width:100%;border-radius:6px" />
                    </b-modal>
                  </div>
                  <div v-else>
                    <input class="form-control" ref="header_banner" name="header_banner" type="file" accept="image/*">
                    <button v-if="templateHeaderMediaUrl" type="button" class="btn btn-sm btn-link mt-1 p-0" @click="headerFileChanged = false">
                      Usar imagem padrão
                    </button>
                  </div>
                </template>

                <!-- VIDEO -->
                <template v-else-if="headerFormat === 'VIDEO'">
                  <div v-if="templateHeaderMediaUrl && !headerFileChanged" class="mb-2">
                    <video
                      :src="templateHeaderMediaUrl"
                      controls
                      style="max-height:150px;border-radius:6px;border:1px solid #e9ecef;display:block"
                    ></video>
                    <small class="text-muted d-block mt-1">Vídeo padrão do template</small>
                    <button type="button" class="btn btn-sm btn-outline-secondary mt-1" @click="headerFileChanged = true">
                      <i class="bx bx-video-plus mr-1"></i> Trocar vídeo
                    </button>
                  </div>
                  <div v-else>
                    <input class="form-control" ref="header_banner" name="header_banner" type="file" accept="video/mp4,video/3gp,video/*">
                    <button v-if="templateHeaderMediaUrl" type="button" class="btn btn-sm btn-link mt-1 p-0" @click="headerFileChanged = false">
                      Usar vídeo padrão
                    </button>
                  </div>
                </template>

                <!-- DOCUMENT -->
                <template v-else-if="headerFormat === 'DOCUMENT'">
                  <div v-if="templateHeaderMediaUrl && !headerFileChanged" class="mb-2">
                    <a :href="templateHeaderMediaUrl" target="_blank" class="btn btn-sm btn-outline-secondary">
                      <i class="bx bx-file mr-1"></i> Ver documento do template
                    </a>
                    <small class="text-muted d-block mt-1">Documento padrão do template</small>
                    <button type="button" class="btn btn-sm btn-outline-secondary mt-1" @click="headerFileChanged = true">
                      <i class="bx bx-upload mr-1"></i> Trocar documento
                    </button>
                  </div>
                  <div v-else>
                    <input class="form-control" ref="header_banner" name="header_banner" type="file" accept=".pdf,.doc,.docx,application/pdf">
                    <button v-if="templateHeaderMediaUrl" type="button" class="btn btn-sm btn-link mt-1 p-0" @click="headerFileChanged = false">
                      Usar documento padrão
                    </button>
                  </div>
                </template>

                <div
                  v-if="showHeader && !templateHeaderMediaUrl && !headerFileChanged"
                  class="invalid-feedback d-block"
                >
                  Template escolhido exige {{ headerFormatLabel.toLowerCase() }} no HEADER.
                </div>
              </div>
            </div>
          </div>
          <!-- Custom template variables -->
          <div class="row" v-if="templateCustomVars.length > 0">
            <div class="col-sm-12">
              <div class="form-group">
                <label>Variáveis personalizadas</label>
                <p class="text-muted font-size-12 mb-2">Preencha as variáveis que serão enviadas para todos os destinatários.</p>
                <div v-for="v in templateCustomVars" :key="v.pos" class="mb-2">
                  <label class="font-size-12"><span v-text="'{{' + v.pos + '}}'"></span> — variável personalizada</label>
                  <input type="text" class="form-control" v-model="v.value" placeholder="Digite o valor..." />
                </div>
              </div>
            </div>
          </div>
          <div class="row" style="margin-top: 1rem">
            <div class="col-sm-3">
              <div style="display:flex; align-items: start; gap: 5px;margin-top: 0.55rem">
                <span style="font-weight: 500;margin-top:2px">Agendar disparo</span>
                <b-form-checkbox v-model="campaign.has_scheduled_at" switch size="lg"></b-form-checkbox>
              </div>
            </div>
            <div class="col-sm-4" v-if="campaign.has_scheduled_at">
              <!-- <label for="scheduled_at">Data do Agendamento</label> -->
              <date-picker
                v-model="campaign.scheduled_at"
                type="datetime"
                :first-day-of-week="1"
                lang="pt-br"
                format="DD/MM/YYYY HH:mm"
                name="scheduled_at"
              >
              </date-picker>
            </div>
          </div>
        </form>
      </div>
    </div>
    <template v-slot:modal-footer>
      <b-button variant="secondary" @click="close()">Cancelar</b-button>
      <button type="submit" class="btn btn-primary mr-1" @click="saveCampaign()">Salvar</button>
    </template>
  </b-modal>
</template>
<script>
// Import component
import Loading from 'vue-loading-overlay'
// Import stylesheet
import 'vue-loading-overlay/dist/vue-loading.css'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/pt-br'
import { mapActions, mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import http from '@/http'
import CampaignFilterBuilder from '@/components/modals/filters/CampaignFilterBuilder.vue'

export default {
  components: {
    Loading,
    DatePicker,
    CampaignFilterBuilder
  },
  props: {
    idcampaign: {
      type: Number || String,
      default: null
    }
  },
  data () {
    return {
      title: 'Nova Campanha',
      showHeader: false,
      headerFormat: null,
      templateHeaderMediaUrl: null,
      isDynamicQrTemplate: false,
      headerFileChanged: false,
      showImagePreview: false,
      templateCustomVars: [],
      loading: true,
      fullPage: true,
      submitted: false,
      campaign: {
        rules: []
      },
      filterCatalog: [],
      catalogLoading: false,
      modal: false,
      participantCount: 0,
      countLoading: false,
      countTimer: null
    }
  },
  validations: {
    campaign: {
      name: { required },
      type_contact: { required },
      template_id: { required }
    }
  },
  methods: {
    ...mapActions('settings', { GetStatus: 'GetStatusSelect', GetRefStatus: 'GetRefStatusList' }),
    ...mapActions('campaign', {
      getCampaign: 'show',
      postData: 'save'
    }),
    ...mapActions('template', {
      getTemplatesAll: 'getAll'
    }),
    ...mapActions('tag', {
      getAll: 'getAll'
    }),
    ...mapActions('group', {
      getAllGroups: 'getAll'
    }),
    setTemplate (templateId) {
      if (templateId) {
        const template = this.templates.find((t) => t.id === this.campaign.template_id)
        if (template) {
          const header = template.components && template.components[0]
          this.headerFormat = (header && header.format) || null
          this.showHeader = !!this.headerFormat
          // Template marcado como QR dinâmico do participante: NÃO mostra
          // upload de banner — backend resolve automaticamente no envio.
          this.isDynamicQrTemplate = template.dynamic_header_type === 'participant_qrcode'
          const mediaUrlMap = {
            IMAGE: template.header_image_url || null,
            VIDEO: template.header_video_url || null,
            DOCUMENT: template.header_document_url || null
          }
          this.templateHeaderMediaUrl = this.headerFormat ? (mediaUrlMap[this.headerFormat] || null) : null
          this.headerFileChanged = false
          // Detect custom variables
          const vars = template.variables || {}
          this.templateCustomVars = Object.entries(vars)
            .filter(([, key]) => key === 'custom')
            .map(([pos]) => ({ pos: parseInt(pos), value: '' }))
            .sort((a, b) => a.pos - b.pos)
        }
      }
    },
    showExampleTemplate () {
      const template = this.templates.filter((t) => t.id === this.campaign.template_id)[0]
      if (template) {
        this.$emit('exampleTemplate', {
          components: template.components,
          header_image_url: template.header_image_url,
          header_video_url: template.header_video_url,
          header_document_url: template.header_document_url
        })
      }
    },
    loadItem (id) {
      if (id) {
        this.title = 'Editar Campanha'
        this.getCampaign(id).then((data) => {
          this.campaign = this.data
          // Hidrata rules a partir de filters JSON (novo) ou filters_with_fallback (legado)
          const fwf = (this.data && (this.data.filters || this.data.filters_with_fallback)) || null
          this.campaign.rules = (fwf && Array.isArray(fwf.rules)) ? fwf.rules : []
        }).then(() => {
          this.loading = false
        })
      }
    },
    fetchFilterCatalog () {
      if (this.filterCatalog.length > 0) return
      this.catalogLoading = true
      http.get('/admin/campaigns/filters/available').then(res => {
        this.filterCatalog = (res.data && res.data.data) || []
      }).catch(() => {
        this.filterCatalog = []
      }).finally(() => {
        this.catalogLoading = false
      })
    },
    getTags (filter) {
      let params = {
        search: this.search || null,
        limit: 100,
        page: 1
      }
      this.params = params
      this.getAll(this.params).then((res) => {
        // this.participants = res.data
      }).catch(error => {
        console.log(error)
      })
    },
    getTemplates () {
      let params = {
        search: this.search || null,
        status: 1,
        limit: 100,
        page: 1
      }
      this.params = params
      this.getTemplatesAll(this.params).then((res) => {
      }).catch(error => {
        console.log(error)
      })
    },
    getGroups () {
      let params = {
        search: this.search || null,
        status: 1,
        limit: 100,
        page: 1
      }
      this.params = params
      this.getAllGroups(this.params).then((res) => {
      }).catch(error => {
        console.log(error)
      })
    },
    open (id = null) {
      this.getTags()
      this.getTemplates()
      this.getGroups()
      this.GetStatus()
      this.GetRefStatus()
      this.fetchFilterCatalog()
      this.modal = true
      this.loading = true
      if (id) {
        this.loadItem(id)
        return
      }
      // Garante que rules sempre é array em campanhas novas
      if (!Array.isArray(this.campaign.rules)) {
        this.$set(this.campaign, 'rules', [])
      }
      this.loading = false
    },
    async saveCampaign () {
      this.submitted = true
      this.$v.campaign.$touch()
      if (this.$v.campaign.$invalid) {
        window.scroll(0, 0)
        return
      }
      this.loading = true
      if (this.campaign.has_scheduled_at) {
        this.campaign.scheduled_at = this.$moment(this.campaign.scheduled_at).format('YYYY-MM-DD HH:mm:ss')
      }

      // Skip validação de banner quando o template usa QR dinâmico — backend
      // resolve por participante; nada precisa ser anexado.
      if (!this.isDynamicQrTemplate) {
        if (this.showHeader && !this.templateHeaderMediaUrl && this.headerFileChanged && this.$refs.header_banner && !this.$refs.header_banner.files[0]) {
          errorMessage('Alerta: Você precisa anexar ' + (this.headerFormat === 'IMAGE' ? 'uma imagem' : this.headerFormat === 'VIDEO' ? 'um vídeo' : 'um documento'))
          return
        }
        if (this.showHeader && !this.templateHeaderMediaUrl && !this.headerFileChanged && (!this.$refs.header_banner || !this.$refs.header_banner.files[0])) {
          errorMessage('Alerta: Você precisa anexar ' + (this.headerFormat === 'IMAGE' ? 'uma imagem' : this.headerFormat === 'VIDEO' ? 'um vídeo' : 'um documento'))
          return
        }
      }

      const formData = new FormData()
      formData.append('id', this.campaign.id || '')
      formData.append('name', this.campaign.name)
      formData.append('type_contact', this.campaign.type_contact)
      // type_filter e select_filter construídos automaticamente no backend a partir de rules
      formData.append('template_id', this.campaign.template_id)
      formData.append('rules', JSON.stringify(this.campaign.rules || []))
      formData.append('has_scheduled_at', this.campaign.has_scheduled_at)
      formData.append('scheduled_at', this.campaign.scheduled_at)
      if (this.showHeader && !this.isDynamicQrTemplate) {
        if (this.headerFileChanged && this.$refs.header_banner && this.$refs.header_banner.files[0]) {
          formData.append('header_banner', this.$refs.header_banner.files[0])
        } else if (this.templateHeaderMediaUrl && !this.headerFileChanged) {
          formData.append('header_banner_url', this.templateHeaderMediaUrl)
        }
      }
      if (this.templateCustomVars.length > 0) {
        const customParams = {}
        this.templateCustomVars.forEach(v => { customParams[v.pos] = v.value })
        formData.append('custom_params', JSON.stringify(customParams))
      }

      await this.postData(formData).then(res => {
        let message = 'Sucesso: Dados armazenados com sucesso.'
        let delay = 2500
        successMessage(message, delay)
        this.close()
        this.$emit('created')
      }).catch((err) => {
        errorMessage('Alerta: ' + err.message)
        this.loading = false
      })
    },
    close () {
      this.modal = false
      this.campaign = { rules: [] }
      this.participantCount = 0
      this.countLoading = false
      this.headerFormat = null
      this.templateHeaderMediaUrl = null
      this.headerFileChanged = false
      this.showHeader = false
      this.templateCustomVars = []
      this.$v.$reset()
    },
    fetchCount () {
      if (this.countTimer) clearTimeout(this.countTimer)
      if (!this.campaign.type_contact) {
        this.participantCount = 0
        return
      }
      this.countTimer = setTimeout(() => {
        this.countLoading = true
        http.post('/admin/campaigns/count-participants', {
          type_contact: this.campaign.type_contact,
          rules: JSON.stringify(this.campaign.rules || [])
        }).then(res => {
          this.participantCount = res.data.count || 0
          this.countLoading = false
        }).catch(() => {
          this.participantCount = 0
          this.countLoading = false
        })
      }, 400)
    }
  },
  computed: {
    ...mapState('settings', ['statusSelect', 'refstatusList']),
    ...mapState('campaign', { data: 'campaign' }),
    ...mapState('template', ['templates']),
    ...mapState('tag', ['tags']),
    ...mapState('group', ['groups']),
    headerFormatLabel () {
      const map = { IMAGE: 'Imagem', VIDEO: 'Vídeo', DOCUMENT: 'Documento' }
      return map[this.headerFormat] || 'Mídia'
    }
  },
  watch: {
    modal () {
      if (!this.modal) {
        this.close()
      }
    },
    'campaign.type_contact' () { this.fetchCount() },
    'campaign.rules': {
      deep: true,
      handler () { this.fetchCount() }
    }
  }
}
</script>
<style scoped>
.vld-overlay.is-full-page {
    z-index: 1051 !important;
}

.cf-qr-info {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: rgba(85, 110, 230, 0.05);
  border: 1px solid rgba(85, 110, 230, 0.2);
  border-radius: 10px;
  margin-bottom: 16px;
}
.cf-qr-info-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  background: #fff;
  border-radius: 8px;
  color: #556ee6;
  font-size: 22px;
}
.cf-qr-info-title { font-size: 13px; font-weight: 600; color: #1a1d26; margin-bottom: 4px; }
.cf-qr-info-desc { font-size: 12px; color: #667085; line-height: 1.5; }
.count-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #f4f5f7;
  border-radius: 6px;
  font-size: 14px;
  color: #495057;
  margin-top: 4px;
  margin-bottom: 16px;
}
.count-box i.bx-group {
  font-size: 18px;
  color: #556ee6;
}
</style>
